import type { AxiosRequestConfig } from 'axios'
import type { ApiResponse } from '@/utils/api/models/api-response'
import axios from '@/utils/api/axios-instance'
import qs from 'qs'

/**
 * 所有 API Service 的基底類別。
 *
 * 各 Service 繼承此類別以取得統一的 HTTP 方法（GET、POST、PUT、DELETE）。
 * 建構子接受 `apiUrl` 作為 base URL，子類別可在 `super()` 傳入不同網址，
 * 以支援多個後端服務或第三方 API。
 *
 * @example
 * ```ts
 * export class UserService extends ApiFactory {
 *   constructor() { super(env.apiBaseUrl) }
 * }
 * ```
 */
export class ApiFactory {
  /** 各 HTTP 方法的 base URL；子類別可在 constructor 傳入不同值以覆蓋 */
  protected apiUrl: string
  constructor(apiUrl: string) {
    this.apiUrl = apiUrl
  }

  /**
   * 發送 GET 請求。
   * @param url - 相對於 `apiUrl` 的路徑（不含前置斜線）
   * @param params - query string 參數；陣列以 `repeat` 格式序列化（e.g. `a=1&a=2`）
   * @param config - 額外的 Axios 請求設定，可覆蓋預設值
   * @returns 後端回應的 Promise
   */
  protected get<Params, Response = ApiResponse>(
    url: string,
    params?: Params,
    config?: AxiosRequestConfig
  ): Promise<Response> {
    // axios 的回傳型別預設是 AxiosResponse<T>，但 axios-instance.ts 的 response 攔截器
    // 已經把 .data 解開了，執行期拿到的本來就是 Response；這裡用 Response 這個外層泛型
    // 的斷言只是修正型別標註跟攔截器實際行為的落差，不是真的改變執行期行為
    return axios.get(`${this.apiUrl}/${url}`, {
      params,
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      ...config
    }) as unknown as Promise<Response>
  }

  /**
   * 發送 POST 請求。
   * @remarks body 會先經 `JSON.stringify` 序列化。Axios 對「本身就是合法 JSON」的純字串
   * body（例如純數字字串 "1"）會直接原樣送出、不加引號，導致後端收到 JSON number 而非
   * string，因此純字串 body 一律先手動序列化，繞過 Axios 這個猜測行為（OP#2464）。
   * @param url - 相對於 `apiUrl` 的路徑
   * @param body - 請求 body
   * @param config - 額外的 Axios 請求設定
   * @returns 後端回應的 Promise
   */
  protected post<Body, Response = ApiResponse>(
    url: string,
    body?: Body,
    config?: AxiosRequestConfig
  ): Promise<Response> {
    return axios.post(
      `${this.apiUrl}/${url}`,
      body === undefined ? undefined : JSON.stringify(body),
      config
    ) as unknown as Promise<Response>
  }

  /**
   * 發送 PUT 請求。
   * @remarks body 會先經 `JSON.stringify` 序列化，確保巢狀物件正確傳遞。
   * @param url - 相對於 `apiUrl` 的路徑
   * @param body - 請求 body
   * @param config - 額外的 Axios 請求設定
   * @returns 後端回應的 Promise
   */
  protected put<Body, Response = ApiResponse>(
    url: string,
    body: Body,
    config?: AxiosRequestConfig
  ): Promise<Response> {
    return axios.put(
      `${this.apiUrl}/${url}`,
      JSON.stringify(body),
      config
    ) as unknown as Promise<Response>
  }

  /**
   * 發送 DELETE 請求。
   * @remarks body 會先經 `JSON.stringify` 序列化，理由同 `post()`（OP#2464）。
   * @param url - 相對於 `apiUrl` 的路徑
   * @param body - 請求 body（放於 `data` 欄位，部分後端需要）
   * @param config - 額外的 Axios 請求設定
   * @returns 後端回應的 Promise
   */
  protected delete<Body, Response = ApiResponse>(
    url: string,
    body: Body,
    config?: AxiosRequestConfig
  ): Promise<Response> {
    return axios.delete(`${this.apiUrl}/${url}`, {
      data: JSON.stringify(body),
      ...config
    }) as unknown as Promise<Response>
  }
}
